import { parseDate } from '@internationalized/date';
import { CHART_NO_FOCUS_OUTLINE } from '@orn/shared-web/components/Chart';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import AnimationFixToolTip from '@orn/shared-web/components/Tooltip/AnimationFixToolTip';
import * as React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import {
  dayToChartX,
  listCalendarDays,
  valueAxisTicks,
  type IDailyValue,
  type ValueOverTimeView,
} from '../../../utils/account/balanceHistory';
import {
  formatCalendarDate,
  formatChartPeriodLong,
  formatChartPeriodShort,
  formatMoneyShort,
  toCurrency,
} from '../../../utils/formatting';
import { ChartCard } from '../ChartCard';
import { ChartHeader } from '../ChartHeader';
import { ChartSeriesLineSwatch, ChartSeriesTooltip, ChartSeriesTooltipRow } from '../ChartSeriesTooltip';
import { calculateXAxisTicks } from '../utils';

const CHART_HEIGHT_PX = 242;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const Y_AXIS_WIDTH_PX = 60;

interface IChartDatum extends IDailyValue {
  x: number;
}

interface IValueTooltipContentProps {
  active?: boolean;
  payload?: Array<{ payload: IChartDatum }>;
}

const ValueTooltipContent = ({ active, payload }: IValueTooltipContentProps) => {
  const datum = payload?.[0]?.payload;
  if (!active || !datum) {
    return null;
  }

  return (
    <ChartSeriesTooltip title={formatChartPeriodLong(datum.date, 'day')}>
      <ChartSeriesTooltipRow
        swatch={<ChartSeriesLineSwatch className="text-utility-brand-500" />}
        label="Value"
        value={toCurrency(datum.value)}
      />
    </ChartSeriesTooltip>
  );
};

interface IValuePlotProps {
  points: readonly IDailyValue[];
  containerWidth: number;
}

const ValuePlot = ({ points, containerWidth }: IValuePlotProps) => {
  const gradientId = `valueOverTime${React.useId().replace(/:/g, '')}`;
  const firstDay = points[0].date;
  const lastDay = points[points.length - 1].date;
  const isSinglePoint = points.length === 1;

  const data = React.useMemo<IChartDatum[]>(
    () => points.map((point) => ({ ...point, x: dayToChartX(point.date) })),
    [points]
  );

  const tickDayByX = React.useMemo(() => {
    const tickDays = calculateXAxisTicks({
      periods: listCalendarDays(firstDay, lastDay),
      containerWidth,
      dateRange: { start: parseDate(firstDay), end: parseDate(lastDay) },
      yAxisWidth: Y_AXIS_WIDTH_PX,
    });
    return new Map(tickDays.map((day) => [dayToChartX(day), day]));
  }, [firstDay, lastDay, containerWidth]);

  const yTicks = React.useMemo(() => valueAxisTicks(points.map((point) => point.value)), [points]);
  const xDomain = isSinglePoint
    ? [data[0].x - ONE_DAY_MS, data[0].x + ONE_DAY_MS]
    : [data[0].x, data[data.length - 1].x];
  const referenceYear = parseDate(lastDay).year;

  return (
    <div style={{ width: '100%', height: `${CHART_HEIGHT_PX}px` }}>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT_PX} className={CHART_NO_FOCUS_OUTLINE}>
        <AreaChart
          data={data}
          margin={{ left: -8, right: 20, top: 5, bottom: 5 }}
          className="text-tertiary [&_.recharts-text]:text-xs"
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="currentColor" className="text-utility-brand-700" stopOpacity={0.12} />
              <stop offset="100%" stopColor="currentColor" className="text-utility-brand-700" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid
            vertical={false}
            horizontal={true}
            stroke="currentColor"
            strokeDasharray="0"
            className="text-utility-gray-100"
          />

          <XAxis
            fill="currentColor"
            dataKey="x"
            type="number"
            domain={xDomain}
            axisLine={false}
            tickLine={false}
            tickMargin={12}
            ticks={Array.from(tickDayByX.keys())}
            interval={0}
            tickFormatter={(x: number) => {
              const day = tickDayByX.get(x);
              return day ? formatChartPeriodShort(day, 'day', referenceYear) : '';
            }}
          />

          <YAxis
            fill="currentColor"
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            width={Y_AXIS_WIDTH_PX}
            domain={[yTicks[0], yTicks[yTicks.length - 1]]}
            ticks={yTicks}
            interval={0}
            tickFormatter={(value: number) => formatMoneyShort(value)}
          />

          <AnimationFixToolTip
            content={<ValueTooltipContent />}
            cursor={{ className: 'stroke-utility-brand-500 stroke-2' }}
          />

          <Area
            isAnimationActive={false}
            dataKey="value"
            name="Value"
            type="monotone"
            stroke="currentColor"
            strokeWidth={3}
            className="text-utility-brand-500"
            fill={`url(#${gradientId})`}
            fillOpacity={1}
            dot={isSinglePoint ? { r: 5, className: 'fill-utility-brand-500 stroke-bg-primary stroke-2' } : false}
            activeDot={{ className: 'fill-bg-primary stroke-utility-brand-500 stroke-2' }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

interface IChartMessageProps {
  children: React.ReactNode;
}

const ChartMessage = ({ children }: IChartMessageProps) => (
  <div
    className="text-tertiary flex items-center justify-center text-center text-sm"
    style={{ height: `${CHART_HEIGHT_PX}px` }}
  >
    {children}
  </div>
);

function singlePointCaption(view: Extract<ValueOverTimeView, { point: IDailyValue }>): string {
  switch (view.kind) {
    case 'noNewValues':
      return 'No new values in this date range.';
    case 'oneValue':
      return 'One value in this date range.';
    default: {
      const started = `Tracking started ${formatCalendarDate(parseDate(view.point.date), 'long')}.`;
      return view.reachesToday ? `${started} The line fills in as new values arrive.` : started;
    }
  }
}

export interface IValueOverTimeChartProps {
  /** The state to draw; null while the first result is loading. */
  view: ValueOverTimeView | null;
  hasError?: boolean;
  className?: string;
}

/**
 * "Value over time" card for an account whose history comes from recorded balance snapshots
 * rather than transactions. Pure: the caller fetches and passes the `view`.
 *
 * Draws an area chart on a true time axis, so a gap between snapshots keeps its slope, with a
 * y-axis fitted to the values. With fewer than two points it draws the lone value as a dot over
 * a caption, or a message when there is nothing to plot. It has no range control of its own.
 */
const ValueOverTimeChart = ({ view, hasError = false, className = '' }: IValueOverTimeChartProps) => {
  const { containerWidth, containerRef } = useContainerWidth();

  const renderBody = () => {
    if (hasError) {
      return <ChartMessage>We couldn&apos;t load this account&apos;s value history.</ChartMessage>;
    }
    if (view === null) {
      return (
        <ChartMessage>
          <LoadingIndicator size="md" />
        </ChartMessage>
      );
    }

    switch (view.kind) {
      case 'awaitingFirstValue':
        return <ChartMessage>We&apos;ll start charting this account&apos;s value after its first update.</ChartMessage>;
      case 'emptyRange':
        return <ChartMessage>No values in this date range.</ChartMessage>;
      case 'line':
        return <ValuePlot points={view.points} containerWidth={containerWidth} />;
      default:
        return (
          <div className="flex flex-col gap-3">
            <ValuePlot points={[view.point]} containerWidth={containerWidth} />
            <div className="text-tertiary text-center text-sm">{singlePointCaption(view)}</div>
          </div>
        );
    }
  };

  return (
    <ChartCard
      containerRef={containerRef}
      className={className}
      contentClassName="pb-8"
      header={<ChartHeader title="Value over time" />}
    >
      {renderBody()}
    </ChartCard>
  );
};

export default ValueOverTimeChart;
